import { DATA_TYPE_SET } from "store/slice/DisplayData/DisplayDataType"
import {
  NodeResult,
  NodeResultPending,
  NodeResultSuccess,
  RUN_STATUS,
  StartedPipeline,
} from "store/slice/Pipeline/PipelineType"
import {
  isNodeResultPending,
  isStartedPipeline,
  isNodeResultSuccess,
} from "store/slice/Pipeline/PipelineUtils"
import { RootState } from "store/store"

export const selectPipelineLatestUid = (state: RootState) => {
  return state.pipeline.currentPipeline?.uid
}

export const selectCurrentPipelineName = (state: RootState) => {
  const uid = selectPipelineLatestUid(state)
  if (uid) {
    const experiments = state.experiments
    if (experiments.status === "fulfilled") {
      return experiments.experimentList?.[uid]?.name
    } else {
      return undefined
    }
  } else {
    return undefined
  }
}

export const selectStartedPipeline = (state: RootState) => {
  return state.pipeline.run
}

export const selectRunOutputPaths = (nodeId: string) => (state: RootState) => {
  const run = selectStartedPipeline(state) as StartedPipeline
  return (run.runResult[nodeId] as NodeResultSuccess)?.outputPaths
}

export const selectPipelineRunBtn = (state: RootState) => {
  return state.pipeline.runBtn
}

export const selectRunResultPendingList = (state: RootState) => {
  const pipeline = selectStartedPipeline(state)
  if (isStartedPipeline(pipeline)) {
    return Object.values(pipeline.runResult).filter(isNodeResultPending)
  } else {
    return []
  }
}

export const selectRunResultPendingNodeIdList = (state: RootState) => {
  const pipeline = selectStartedPipeline(state)
  if (isStartedPipeline(pipeline)) {
    return Object.entries(pipeline.runResult)
      .map(([nodeId, nodeResult]) => ({ nodeId, nodeResult }))
      .filter(isNodeResultPendingAndNodeId)
      .map(({ nodeId }) => nodeId)
  } else {
    return []
  }
}

function isNodeResultPendingAndNodeId(arg: {
  nodeId: string
  nodeResult: NodeResult
}): arg is {
  nodeId: string
  nodeResult: NodeResultPending
} {
  return isNodeResultPending(arg.nodeResult)
}

export const selectPipelineStatus = (state: RootState) => {
  const pipeline = selectStartedPipeline(state)
  return pipeline.status
}

export const selectPipelineIsCanceled = (state: RootState) => {
  const pipeline = selectStartedPipeline(state)
  return pipeline.status === RUN_STATUS.CANCELED
}

export const selectPipelineIsStartedSuccess = (state: RootState) => {
  const pipeline = selectStartedPipeline(state)
  return pipeline.status === RUN_STATUS.START_SUCCESS
}

export const selectPipelineNodeResultSuccessList = (state: RootState) => {
  const pipeline = selectStartedPipeline(state)
  if (isStartedPipeline(pipeline)) {
    return Object.entries(pipeline.runResult)
      .map(([nodeId, nodeResult]) => {
        return {
          nodeId,
          nodeResult,
        }
      })
      .filter(isNodeResultSuccessAndNodeId)
  } else {
    return []
  }
}

// selectPipelineNodeResultSuccessListの返り値の型を正しく認識させるためだけに作った
function isNodeResultSuccessAndNodeId(arg: {
  nodeId: string
  nodeResult: NodeResult
}): arg is {
  nodeId: string
  nodeResult: NodeResultSuccess
} {
  return isNodeResultSuccess(arg.nodeResult)
}

export const selectPipelineNodeResultStatus =
  (nodeId: string) => (state: RootState) => {
    const pipeline = selectStartedPipeline(state)
    if (isStartedPipeline(pipeline)) {
      if (Object.keys(pipeline.runResult).includes(nodeId)) {
        return pipeline.runResult[nodeId].status
      }
    }
    return null
  }

export const selectPipelineNodeResultMessage =
  (nodeId: string) => (state: RootState) => {
    const pipeline = selectStartedPipeline(state)
    if (isStartedPipeline(pipeline)) {
      if (Object.keys(pipeline.runResult).includes(nodeId)) {
        return pipeline.runResult[nodeId].message
      }
    }
    return null
  }

export const selectPipelineNodeResultOutputKeyList =
  (nodeId: string) => (state: RootState) => {
    const pipeline = selectStartedPipeline(state)
    if (isStartedPipeline(pipeline)) {
      const nodeResult = pipeline.runResult[nodeId]
      if (
        Object.keys(pipeline.runResult).includes(nodeId) &&
        isNodeResultSuccess(nodeResult)
      ) {
        return Object.keys(nodeResult.outputPaths)
      }
    }
    return []
  }

const selectPipelineNodeResultOutputPaths =
  (nodeId: string) => (state: RootState) => {
    const pipeline = selectStartedPipeline(state)
    if (isStartedPipeline(pipeline)) {
      const nodeResult = pipeline.runResult[nodeId]
      if (
        Object.keys(pipeline.runResult).includes(nodeId) &&
        isNodeResultSuccess(nodeResult)
      ) {
        return nodeResult.outputPaths
      }
    }
    return null
  }

export const selectPipelineNodeResultOutputPathsExists =
  (nodeId: string) => (state: RootState) => {
    const outputPaths = selectPipelineNodeResultOutputPaths(nodeId)(state)

    if (outputPaths == null) {
      return false
    }

    const firstOutputPath = Object.values(outputPaths).at(0)
    const outputPathsExists =
      firstOutputPath != null && firstOutputPath.type !== DATA_TYPE_SET.EMPTY
    return outputPathsExists
  }

export const selectPipelineNodeResultOutputFilePath =
  (nodeId: string, outputKey: string) => (state: RootState) => {
    const outputPaths = selectPipelineNodeResultOutputPaths(nodeId)(state)
    if (outputPaths && Object.keys(outputPaths).includes(outputKey)) {
      return outputPaths[outputKey].path
    } else {
      throw new Error(`key error. outputKey:${outputKey}`)
    }
  }

export const selectOutputFilePathCellRoi =
  (nodeId?: string | null) => (state: RootState) => {
    if (!nodeId) return ""
    const outputPaths = selectPipelineNodeResultOutputPaths(nodeId)(state)
    if (outputPaths && Object.keys(outputPaths).includes("cell_roi")) {
      return outputPaths["cell_roi"].path
    }
    return ""
  }

export const selectPipelineNodeResultOutputFileDataType =
  (nodeId: string, outputKey: string) => (state: RootState) => {
    const outputPaths = selectPipelineNodeResultOutputPaths(nodeId)(state)
    if (outputPaths && Object.keys(outputPaths).includes(outputKey)) {
      return outputPaths[outputKey].type
    } else {
      throw new Error(`key error. outputKey:${outputKey}`)
    }
  }
